import { API_CONFIG, STORAGE_KEYS, API_ENDPOINTS, USER_ROLES, SOCKET_KEYS, APP_NAME } from '../helpers/constants';
import { capitalizeFirstLetter } from '../helpers/stringUtils';
import socketService from './socketService';

// Channel member interface based on the API response
export interface ChannelMember {
  id: string;
  name: string;
  username: string;
  email: string;
  company_name?: string;
  status: 'active' | 'pending' | 'inactive';
  role: string;
  avatarInitials?: string;
  avatarColor?: string;
  created_datetime?: string;
  updated_datetime?: string;
}

export interface ChannelMembersResponse {
  members: ChannelMember[];
  total: number;
  page: number;
  size: number;
}

export interface CreateChannelMemberRequest {
  mail_id: string;
  role: string;
  permissions?: {
    [key: string]: any;
  };
}

export interface BulkChannelMemberEntry {
  mail_id: string;
  role: 'User' | 'Admin';
  permissions?: {
    [key: string]: any;
  };
}

export interface BulkChannelMemberFailure {
  mail_id: string;
  reason: string;
}

// POST /channels/{id}/members/bulk returns 200 even on partial failure; always check `failed`.
export interface BulkAddChannelMembersResponse {
  items: any[];
  total: number;
  message: string | null;
  // Legacy sentence form of `failed`; kept by the backend for compatibility
  errors: string[] | null;
  // Optional so an older backend without these fields still works
  failed?: BulkChannelMemberFailure[];
  already_members?: string[];
}

export interface UpdateChannelMemberRequest {
  role: string;
  permissions?: {
    [key: string]: any;
  };
}

export interface DeleteChannelMemberResponse {
  success: boolean;
  message: string;
}

export interface Workspace {
  id: string;
  name: string;
  description: string;
  channel_count: number;
  created_at: string;
  tags?: any[];
}

export interface WorkspacesResponse {
  tags: Workspace[];
  total: number;
  page: number;
  size: number;
}

class ChannelService {


  /**
   * Generic function to check if the logged-in user has admin role
   * @returns {boolean} true if user is admin, false otherwise
   */
  isUserAdmin(): boolean {
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        return userData.role === USER_ROLES.ADMIN;
      }
      return false;
    } catch (error) {
      console.error('Error checking user admin status:', error);
      return false;
    }
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    return {
      'Content-Type': 'application/json',
      'accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  async updateChannel(
    channelId: string,
    data: Record<string, string>
  ): Promise<any> {
    const response = await fetch(
      `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channelId)}`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to update channel: ${response.status}`);
    }
    return response.json();
  }

  async getChannel(
    channelId: string
  ): Promise<any> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.GET_BY_ID(channelId)}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching channel:', error);
      throw error;
    }
  }

  async getChannelMembers(
    channelId: string, 
    page: number = 1, 
    size: number = 50
  ): Promise<ChannelMembersResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST_MEMBERS(channelId)}?page=${page}&size=${size}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform the API response to match our interface
      const transformedMembers = data.items?.map((member: any) => ({
        id: member.id || member.user_id,
        name: capitalizeFirstLetter(member.name || member.full_name) || 'Unknown',
        username: member.username || `@${member.name?.toLowerCase() || 'user'}`,
        email: member.email || member.email_id || '',
        company_name: member.company_name || '',
        status: member.status || 'active',
        role: this.formatRoleDisplay(member.membership_role || 'user'),
        avatarInitials: (member.name || 'U').charAt(0).toUpperCase(),
        avatarColor: this.getAvatarColor(member.name || 'U'),
        created_datetime: member.created_datetime,
        updated_datetime: member.updated_datetime
      })) || [];

      return {
        members: transformedMembers,
        total: data.total || transformedMembers.length,
        page: data.page || page,
        size: data.size || size
      };
    } catch (error) {
      console.error('Error fetching channel members:', error);
      throw error;
    }
  }

  /**
   * Fetch all channel members without pagination by requesting a large page size
   * or iterating pages until all members are retrieved, depending on API support.
   */
  async getChannelMembersAll(channelId: string): Promise<ChannelMember[]> {
    try {
      // First try a single large page size to avoid multiple requests
      const firstPage = await this.getChannelMembers(channelId, 1, 200);
      // If API returns fewer than requested but indicates more pages, fall back to pagination loop
      const collected: ChannelMember[] = [...firstPage.members];
      const total = firstPage.total || collected.length;
      if (collected.length >= total) {
        return collected;
      }
      const pageSize = firstPage.size || 100;
      const totalPages = Math.ceil(total / pageSize);
      for (let page = 2; page <= totalPages; page += 1) {
        const pageResp = await this.getChannelMembers(channelId, page, pageSize);
        collected.push(...pageResp.members);
        if (collected.length >= total) break;
      }
      return collected;
    } catch (error) {
      console.error('Error fetching all channel members:', error);
      throw error;
    }
  }

  /**
   * Fetch channel members without adding page/size query params.
   */
  async getChannelMembersNoPaging(channelId: string): Promise<ChannelMember[]> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST_MEMBERS(channelId)}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const sourceMembers = data.items || data.members || data || [];
      const transformedMembers = (sourceMembers as any[]).map((member: any) => ({
        id: member.id || member.user_id,
        name: capitalizeFirstLetter(member.name || member.full_name) || 'Unknown',
        username: member.username || `@${(member.name || 'user').toLowerCase()}`,
        email: member.email || member.email_id || '',
        company_name: member.company_name || '',
        status: member.status || 'active',
        role: this.formatRoleDisplay(member.membership_role || 'user'),
        avatarInitials: (member.name || 'U').charAt(0).toUpperCase(),
        avatarColor: this.getAvatarColor(member.name || 'U'),
        created_datetime: member.created_datetime,
        updated_datetime: member.updated_datetime,
      }));

      return transformedMembers;
    } catch (error) {
      console.error('Error fetching channel members (no paging):', error);
      throw error;
    }
  }

  async addChannelMember(
    channelId: string, 
    memberData: CreateChannelMemberRequest
  ): Promise<ChannelMember> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.ADD_MEMBER(channelId)}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(memberData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Emit socket event for new member added
      const eventData = {
        type: SOCKET_KEYS.MEMBER_ADDED_TO_CHANNEL,
        data: {
          emailId: memberData.mail_id,
          channelId: channelId
        }
      };
      
      try {
        socketService.sendSocketMessage(eventData);
        
        // Emit CHANNEL_USER_UPDATE event after 500ms to update user count
        setTimeout(() => {
          const refreshEventData = {
            type: SOCKET_KEYS.CHANNEL_USER_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(refreshEventData);
        }, 500);
      } catch (error) {
        // Handle error silently
      }
      
      return this.transformMemberData(data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Extract the email from a bulk-add error string, e.g.
   * "Email ID 'x@abc.com' not found or does not belong to this company" -> "x@abc.com".
   */
  getEmailFromBulkError(error: string): string | null {
    const match = error.match(/'([^']+@[^']+)'/);
    return match ? match[1].toLowerCase() : null;
  }

  /**
   * Failures from a bulk-add response. Uses `failed` when present and falls back to
   * parsing the legacy `errors` strings for an older backend.
   */
  getBulkFailures(response: BulkAddChannelMembersResponse): BulkChannelMemberFailure[] {
    if (Array.isArray(response.failed)) {
      return response.failed;
    }
    return (response.errors || []).map(err => ({
      mail_id: this.getEmailFromBulkError(err) || '',
      reason: err,
    }));
  }

  /**
   * Add several members in one POST /channels/{id}/members/bulk request (max 100).
   * Resolves with 200 even when some emails fail; check `failed` via getBulkFailures().
   * Throws with the backend `detail` message on a non-OK response.
   */
  async addChannelMembersBulk(
    channelId: string,
    members: BulkChannelMemberEntry[],
    platformName?: string
  ): Promise<BulkAddChannelMembersResponse> {
    const response = await fetch(
      `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.ADD_MEMBERS_BULK(channelId)}`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ members, platform_name: platformName }),
      }
    );

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.json();
        if (typeof body?.detail === 'string') {
          message = body.detail;
        } else if (Array.isArray(body?.detail)) {
          // 422 validation error: detail is an array of { msg, ... }
          message = body.detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || message;
        }
      } catch {
        // Non-JSON error body; keep the status message
      }
      throw new Error(message);
    }

    const data: BulkAddChannelMembersResponse = await response.json();

    // Same socket behaviour as addChannelMember: one MEMBER_ADDED event per newly added
    // email (each client only reacts to its own email), then a single user-count refresh.
    try {
      const skippedEmails = new Set(
        [
          ...this.getBulkFailures(data).map(failure => failure.mail_id),
          ...(data.already_members || []),
        ]
          .filter(Boolean)
          .map(email => email.toLowerCase())
      );
      members
        .filter(member => !skippedEmails.has(member.mail_id.toLowerCase()))
        .forEach(member => {
          socketService.sendSocketMessage({
            type: SOCKET_KEYS.MEMBER_ADDED_TO_CHANNEL,
            data: {
              emailId: member.mail_id,
              channelId: channelId
            }
          });
        });

      setTimeout(() => {
        socketService.sendSocketMessage({
          type: SOCKET_KEYS.CHANNEL_USER_UPDATE,
          data: {
            channelId: channelId
          }
        });
      }, 500);
    } catch (error) {
      // Handle error silently
    }

    return data;
  }

  async updateChannelMember(
    channelId: string, 
    memberId: string, 
    memberData: UpdateChannelMemberRequest
  ): Promise<ChannelMember> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE_MEMBER(channelId, memberId)}`,
        {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(memberData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.transformMemberData(data);
    } catch (error) {
      throw error;
    }
  }

  async removeChannelMember(
    channelId: string, 
    memberId: string,
    memberEmailId?: string
  ): Promise<DeleteChannelMemberResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.REMOVE_MEMBER(channelId, memberId)}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Emit socket event for member removed
      if (memberEmailId) {
        const eventData = {
          type: SOCKET_KEYS.MEMBER_REMOVED_FROM_CHANNEL,
          data: {
            emailId: memberEmailId,
            channelId: channelId
          }
        };

        try {
          socketService.sendSocketMessage(eventData);
          
          // Emit CHANNEL_USER_UPDATE event after 500ms to update user count
          setTimeout(() => {
            const refreshEventData = {
              type: SOCKET_KEYS.CHANNEL_USER_UPDATE,
              data: {
                channelId: channelId
              }
            };
            socketService.sendSocketMessage(refreshEventData);
          }, 500);
        } catch (error) {
          // Handle error silently
        }
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async searchUsers(
    companyId: string,
    searchTerm: string
  ): Promise<ChannelMembersResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.SEARCH_MEMBERS(companyId, searchTerm)}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform the API response to match our interface
      const transformedMembers = data.users?.map((user: any) => ({
        id: user.id || user.user_id,
        name: capitalizeFirstLetter(user.name || user.full_name) || 'Unknown User',
        username: user.username || user.user_name || 'unknown',
        email: user.email_id || user.email_address || '',
        company_name: user.company_name || '',
        status: user.status || 'active',
        role: this.formatRoleDisplay(user.role || 'user'),
        avatarInitials: (user.name || user.full_name || 'U').charAt(0).toUpperCase(),
        avatarColor: this.getAvatarColor(user.name || user.full_name || 'U'),
        created_datetime: user.created_datetime,
        updated_datetime: user.updated_datetime
      })) || [];

      return {
        members: transformedMembers,
        total: data.total || transformedMembers.length,
        page: data.page || 1,
        size: data.size || 10
      };
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  async getWorkspaces(
    page: number = 1,
    size: number = 10,
    search?: string,
    sortBy: string = 'name',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<WorkspacesResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.WORKSPACES(page, size, search, sortBy, sortOrder)}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      throw error;
    }
  }

  async updateWorkspace(workspaceId: string, data: { name: string; description: string }): Promise<any> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE_WORKSPACE(workspaceId)}`,
        {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error;
    }
  }

  async deleteWorkspace(workspaceId: string): Promise<any> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.DELETE_WORKSPACE(workspaceId)}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json(); // Assuming it returns something, or we can just return true
    } catch (error) {
      console.error('Error deleting workspace:', error);
      throw error;
    }
  }

  private transformMemberData(data: any): ChannelMember {
    return {
      id: data.id || data.user_id,
              name: capitalizeFirstLetter(data.name || data.full_name) || 'Unknown',
      username: data.username || `@${data.name?.toLowerCase() || 'user'}`,
      email: data.email || data.email_id || '',
      company_name: data.company_name || '',
      status: data.status || 'active',
      role: this.formatRoleDisplay(data.role || 'user'),
      avatarInitials: (data.name || 'U').charAt(0).toUpperCase(),
      avatarColor: this.getAvatarColor(data.name || 'U'),
      created_datetime: data.created_datetime,
      updated_datetime: data.updated_datetime
    };
  }

  private getAvatarColor(name: string): string {
    const colors = [
      '#424242', '#4caf50', '#9c27b0', '#2e7d32', 
      '#00838f', '#757575', '#f57c00', '#d32f2f',
      '#1976d2', '#388e3c', '#7b1fa2', '#c2185b'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  }

  private formatRoleDisplay(role: string): string {
    if (!role) return 'User';
    
    // Convert lowercase role to proper case
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'user':
        return 'User';
      case 'owner':
        return 'Owner';
      default:
        // For any other role, capitalize first letter
        return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    }
  }
}

export default new ChannelService();
